import { useState, useEffect } from 'react';
import BasicModal from './BasicModal';
import { ShareIcon, PlusIcon, XMarkIcon, LinkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { shareTemplateWithColleagues } from '../../services/UserPacketTemplateService';
import { Mixpanel } from '../../services/Mixpanel';
import { LINK_SOURCE } from '../../constants/linkSources';
import { useShareUrl } from '../../hooks/useShareUrl';
import { isValidEmail } from '../../utils/email';

const MAX_COLLEAGUES = 10;

export const ShareTemplateModal = ({
  showModal,
  setShowModal,
  templateId,
  templateTitle,
  source = 'unknown',
}) => {
  const [colleagues, setColleagues] = useState([{ email: '', name: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const buildShareLink = useShareUrl();
  const templateLink = buildShareLink(`${window.location.origin}/t/${templateId}`, {
    linkSource: LINK_SOURCE.COPY_LINK_TEMPLATE,
  });

  // Track modal opened
  useEffect(() => {
    if (showModal) {
      Mixpanel.track('Share Template Modal Opened', {
        template_id: templateId,
        template_title: templateTitle,
        source,
      });
    }
  }, [showModal, templateId, templateTitle, source]);

  const addColleague = () => {
    Mixpanel.track('Share Template Add Recipient Clicked', {
      template_id: templateId,
      template_title: templateTitle,
      current_recipient_count: colleagues.length,
      source,
    });
    setColleagues([...colleagues, { email: '', name: '' }]);
  };

  const removeColleague = (index) => {
    if (colleagues.length > 1) {
      Mixpanel.track('Share Template Remove Recipient Clicked', {
        template_id: templateId,
        template_title: templateTitle,
        current_recipient_count: colleagues.length,
        source,
      });
      setColleagues(colleagues.filter((_, i) => i !== index));
    }
  };

  const updateColleague = (index, field, value) => {
    const updated = [...colleagues];
    updated[index][field] = value;
    setColleagues(updated);
    setError(null);
  };

  const validateForm = () => {
    for (const colleague of colleagues) {
      if (!colleague.email.trim()) {
        Mixpanel.track('Share Template Validation Failed', {
          template_id: templateId,
          template_title: templateTitle,
          error_type: 'missing_email',
          recipient_count: colleagues.length,
          source,
        });
        setError('Please enter an email address for all colleagues.');
        return false;
      }
      if (!isValidEmail(colleague.email)) {
        Mixpanel.track('Share Template Validation Failed', {
          template_id: templateId,
          template_title: templateTitle,
          error_type: 'invalid_email',
          recipient_count: colleagues.length,
          source,
        });
        setError('Please enter valid email addresses.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const validColleagues = colleagues
      .filter((c) => c.email.trim())
      .map((c) => ({
        email: c.email.trim(),
        name: c.name.trim() || undefined,
      }));

    Mixpanel.track('Share Template Submit Clicked', {
      template_id: templateId,
      template_title: templateTitle,
      recipient_count: validColleagues.length,
      source,
    });

    try {
      await shareTemplateWithColleagues(templateId, validColleagues);

      Mixpanel.track('Share Template Success', {
        template_id: templateId,
        template_title: templateTitle,
        recipient_count: validColleagues.length,
        source,
      });

      setSuccess(true);

      setTimeout(() => {
        setShowModal(false);
        setColleagues([{ email: '', name: '' }]);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      Mixpanel.track('Share Template Failed', {
        template_id: templateId,
        template_title: templateTitle,
        recipient_count: validColleagues.length,
        error: err.message,
        source,
      });
      setError(err.message || 'Failed to share template. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      title={success ? undefined : 'Share Template'}
      maxWidth='sm:max-w-2xl'>
      <div className='mt-4'>
        {!success && (
          <p className='text-sm text-gray-600 mb-4'>
            Share <span className='font-semibold'>{templateTitle}</span> with your colleagues.
          </p>
        )}

        {success ? (
          <div className='text-center py-4'>
            <div className='mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 mb-4'>
              <ShareIcon className='h-6 w-6 text-emerald-600' />
            </div>
            <p className='text-lg font-semibold text-gray-900'>Shared!</p>
            <p className='text-sm text-gray-600 mt-2'>
              Your colleagues will receive an email shortly.
            </p>
          </div>
        ) : (
          <>
            {/* Copy Link Section */}
            <div className='mb-6 flex items-center gap-2'>
              <input
                type='text'
                readOnly
                value={templateLink}
                className='flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-600 bg-gray-50'
              />
              <button
                type='button'
                onClick={() => {
                  navigator.clipboard.writeText(templateLink);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                  Mixpanel.track('Share Template Link Copied', {
                    template_id: templateId,
                    template_title: templateTitle,
                    source,
                  });
                }}
                className='flex items-center gap-1 px-3 py-2 text-sm font-medium text-emerald-600 border border-emerald-600 rounded-md hover:bg-emerald-50 transition-colors'>
                {linkCopied ? (
                  <>
                    <CheckIcon className='h-4 w-4' />
                    Copied!
                  </>
                ) : (
                  <>
                    <LinkIcon className='h-4 w-4' />
                    Copy Link
                  </>
                )}
              </button>
            </div>

            {/* Separator */}
            <div className='relative mb-6'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-gray-300' />
              </div>
              <div className='relative flex justify-center text-sm'>
                <span className='bg-white px-2 text-gray-500'>or share by email</span>
              </div>
            </div>

            {/* Email Fields */}
            <div className='space-y-4'>
              {colleagues.map((colleague, index) => (
                <div key={index} className='flex gap-2 items-start'>
                  <div className='flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    <div>
                      <label
                        htmlFor={`template-email-${index}`}
                        className='block text-sm font-medium text-gray-700 mb-1'>
                        Email <span className='text-red-500'>*</span>
                      </label>
                      <input
                        id={`template-email-${index}`}
                        type='email'
                        value={colleague.email}
                        onChange={(e) => updateColleague(index, 'email', e.target.value)}
                        placeholder='colleague@example.com'
                        className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`template-name-${index}`}
                        className='block text-sm font-medium text-gray-700 mb-1'>
                        First Name (optional)
                      </label>
                      <input
                        id={`template-name-${index}`}
                        type='text'
                        value={colleague.name}
                        onChange={(e) => updateColleague(index, 'name', e.target.value)}
                        placeholder='Jane'
                        className='w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500'
                      />
                    </div>
                  </div>
                  {colleagues.length > 1 && (
                    <button
                      type='button'
                      onClick={() => removeColleague(index)}
                      className='mt-7 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md'
                      aria-label='Remove colleague'>
                      <XMarkIcon className='h-5 w-5' />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {colleagues.length < MAX_COLLEAGUES && (
              <button
                type='button'
                onClick={addColleague}
                className='mt-4 flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium'>
                <PlusIcon className='h-4 w-4' />
                Add another colleague
              </button>
            )}

            {error && (
              <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-md'>
                <p className='text-sm text-red-800'>{error}</p>
              </div>
            )}

            <div className='mt-6 flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => {
                  Mixpanel.track('Share Template Cancelled', {
                    template_id: templateId,
                    template_title: templateTitle,
                    recipient_count: colleagues.length,
                    had_input: colleagues.some((c) => c.email.trim() || c.name.trim()),
                    source,
                  });
                  setShowModal(false);
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'>
                Cancel
              </button>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={isSubmitting}
                className='px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'>
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </BasicModal>
  );
};
